import { useEffect, useRef, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Upload, Trash2, ShieldCheck, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Customer-side identity / driver-license uploader. Each kind is a single-slot upload —
 * uploading a new file replaces the existing one (enforced on the backend with a $pull
 * before the $push). Supports PDF + image (jpg/png/webp). Renders a small inline preview
 * for images, a generic PDF chip for documents, and download/remove actions.
 */
const DOCUMENT_TYPES = [
  { kind: "driver_license", label: "Driver's licence (front)", hint: "Front side. Required at pickup." },
  { kind: "driver_license_back", label: "Driver's licence (back)", hint: "Back side. Some vendors require both sides." },
  { kind: "id_card", label: "ID card or passport", hint: "Government-issued photo identification." },
];

export default function CustomerDocumentsCard() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/users/me/documents");
      setDocs(data.documents || []);
    } catch (e) {
      // Non-fatal: surface the empty state to the user and let them re-trigger.
      console.error("customer documents load failed:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const byKind = (kind) => docs.find((d) => d.kind === kind) || null;

  return (
    <div className="rounded-3xl border border-border bg-white p-6 mb-8" data-testid="customer-documents-card">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-9 w-9 rounded-2xl bg-emerald-50 flex items-center justify-center"><ShieldCheck className="h-4 w-4 text-emerald-700" /></div>
        <div>
          <h2 className="font-display text-lg font-medium">Identity documents</h2>
          <p className="text-xs text-neutral-500">Add your driver's licence and an ID. Vendors will verify these at pickup. Files are private and only shared with the vendor of bookings you make.</p>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-neutral-500 mt-4">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
          {DOCUMENT_TYPES.map((t) => (
            <DocumentSlot key={t.kind} type={t} doc={byKind(t.kind)} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentSlot({ type, doc, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isImage = (doc?.content_type || "").startsWith("image/") || /\.(jpg|jpeg|png|webp|gif)$/i.test(doc?.path || "");

  const onPick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/users/me/documents", {
        kind: type.kind,
        path: up.data.path,
        filename: up.data.filename,
        content_type: up.data.content_type,
      });
      toast.success(`${type.label} uploaded`);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!doc) return;
    if (!window.confirm(`Remove your ${type.label.toLowerCase()}?`)) return;
    try {
      await api.delete(`/users/me/documents/${doc.id}`);
      toast.success("Document removed");
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="rounded-2xl border border-border p-4" data-testid={`document-slot-${type.kind}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-medium text-sm">{type.label}</div>
          <div className="text-xs text-neutral-500">{type.hint}</div>
        </div>
        {doc && (
          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold shrink-0">On file</span>
        )}
      </div>
      {doc ? (
        <div>
          <a
            href={fileUrl(doc.path)}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-xl overflow-hidden border border-border mb-3 group"
            data-testid={`document-link-${type.kind}`}
          >
            {isImage ? (
              <img src={fileUrl(doc.path)} alt={type.label} className="w-full h-32 object-cover group-hover:opacity-90 transition" />
            ) : (
              <div className="flex items-center gap-2 p-4 bg-neutral-50">
                <FileText className="h-5 w-5 text-rose-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">{doc.filename || "document.pdf"}</div>
                  <div className="text-[10px] text-neutral-500">Click to open</div>
                </div>
                <ExternalLink className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
              </div>
            )}
          </a>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={onPick} disabled={uploading} className="rounded-full" data-testid={`document-replace-${type.kind}`}>
              {uploading ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Upload className="h-3 w-3 mr-1.5" />}Replace
            </Button>
            <Button size="sm" variant="ghost" onClick={remove} className="rounded-full text-rose-600 hover:text-rose-700" data-testid={`document-remove-${type.kind}`}>
              <Trash2 className="h-3 w-3 mr-1.5" />Remove
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          className="w-full rounded-xl border-2 border-dashed border-border hover:border-blue-400 hover:bg-blue-50/40 transition px-4 py-8 flex flex-col items-center gap-1.5 disabled:opacity-50"
          data-testid={`document-upload-${type.kind}`}
        >
          {uploading ? <Loader2 className="h-5 w-5 text-blue-600 animate-spin" /> : <Upload className="h-5 w-5 text-neutral-400" />}
          <div className="text-sm font-medium">{uploading ? "Uploading…" : "Upload file"}</div>
          <div className="text-[10px] text-neutral-500">JPG, PNG or PDF · max 8 MB</div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
        className="hidden"
        onChange={onFile}
        data-testid={`document-input-${type.kind}`}
      />
    </div>
  );
}
